import { SchemaOf, TestContext, object } from 'yup';

import KubernetesResourceReservationHelper from '@/kubernetes/helpers/resourceReservationHelper';
import { nanNumberSchema } from '@/react-tools/yup-schemas';

import { ResourceQuotaFormValues } from './types';

type NodeLimit = {
  CPU: number;
  Memory: number;
};

type NodesLimits = Record<string, NodeLimit>;

type ValidationData = {
  maxMemoryLimit: number;
  maxCpuLimit: number;
  isEnvironmentAdmin: boolean;
  nodeLimits: NodesLimits;
  isExistingCPUReservationUnchanged: boolean;
  isExistingMemoryReservationUnchanged: boolean;
  isExistingCPURequestUnchanged: boolean;
  isExistingMemoryRequestUnchanged: boolean;
};

export function resourceReservationValidation(
  validationData?: ValidationData
): SchemaOf<ResourceQuotaFormValues> {
  return object().shape({
    memoryRequest: nanNumberSchema('Memory request is required.')
      .min(0, 'Value must be greater than or equal to 0.')
      .test(
        'capacityAvailable',
        `The memory capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the memory capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxMemoryLimit > 0
      )
      .max(validationData?.maxMemoryLimit || 0, ({ value }) =>
        validationData?.isExistingMemoryRequestUnchanged
          ? `Value must be between 0 and ${validationData?.maxMemoryLimit}MB now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxMemoryLimit}MB.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            return true;
          }
          const { cpuRequest } = context.parent;
          return hasSuitableNode(value, cpuRequest, validationData.nodeLimits);
        }
      )
      .required('Memory request is required.'),
    memoryLimit: nanNumberSchema('Memory limit is required.')
      .min(0, 'Value must be greater than or equal to 0.')
      .test(
        'capacityAvailable',
        `The memory capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the memory capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxMemoryLimit > 0
      )
      .max(validationData?.maxMemoryLimit || 0, ({ value }) =>
        // when the existing reservation is unchanged and exceeds the new limit, show a different error message
        // https://portainer.atlassian.net/browse/EE-5933?focusedCommentId=29308
        validationData?.isExistingMemoryReservationUnchanged
          ? `Value must be between 0 and ${validationData?.maxMemoryLimit}MB now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxMemoryLimit}MB.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            // explicitely check for undefined, since 0 is a valid value
            return true;
          }
          const { cpuLimit } = context.parent;
          return hasSuitableNode(value, cpuLimit, validationData.nodeLimits);
        }
      )
      .test(
        'limitGreaterThanRequest',
        'Memory limit must be greater than or equal to memory request',
        (value: number | undefined, context: TestContext) => {
          if (value === undefined) {
            return true;
          }
          const { memoryRequest } = context.parent;
          return value >= memoryRequest;
        }
      )
      .required('Memory limit is required.'),
    cpuRequest: nanNumberSchema('CPU request is required.')
      .min(0)
      .test(
        'capacityAvailable',
        `The CPU capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the CPU capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxCpuLimit > 0
      )
      .max(validationData?.maxCpuLimit || 0, ({ value }) =>
        validationData?.isExistingCPURequestUnchanged
          ? `Value must be between 0 and ${validationData?.maxCpuLimit} now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxCpuLimit}.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            return true;
          }
          const { memoryRequest } = context.parent;
          return hasSuitableNode(
            memoryRequest,
            value,
            validationData.nodeLimits
          );
        }
      )
      .required('CPU request is required.'),
    cpuLimit: nanNumberSchema('CPU limit is required.')
      .min(0)
      .test(
        'capacityAvailable',
        `The CPU capacity for this namespace has been exhausted, so you cannot deploy the application.${
          validationData?.isEnvironmentAdmin
            ? ''
            : ' Contact your administrator to expand the CPU capacity of the namespace.'
        }`,
        () => !!validationData && validationData.maxCpuLimit > 0
      )
      .max(validationData?.maxCpuLimit || 0, ({ value }) =>
        // when the existing reservation is unchanged and exceeds the new limit, show a different error message
        // https://portainer.atlassian.net/browse/EE-5933?focusedCommentId=29308
        validationData?.isExistingCPUReservationUnchanged
          ? `Value must be between 0 and ${validationData?.maxCpuLimit} now - the previous value of ${value} exceeds this.`
          : `Value must be between 0 and ${validationData?.maxCpuLimit}.`
      )
      .test(
        'hasSuitableNode',
        `These reservations would exceed the resources currently available in the cluster.`,
        (value: number | undefined, context: TestContext) => {
          if (!validationData || value === undefined) {
            // explicitely check for undefined, since 0 is a valid value
            return true;
          }
          const { memoryLimit } = context.parent;
          return hasSuitableNode(memoryLimit, value, validationData.nodeLimits);
        }
      )
      .test(
        'limitGreaterThanRequest',
        'CPU limit must be greater than or equal to CPU request',
        (value: number | undefined, context: TestContext) => {
          if (value === undefined) {
            return true;
          }
          const { cpuRequest } = context.parent;
          return value >= cpuRequest;
        }
      )
      .required('CPU limit is required.'),
  });
}

function hasSuitableNode(
  memoryValue: number,
  cpuValue: number,
  nodeLimits: NodesLimits
) {
  const nanParsedMemoryValue = Number.isNaN(memoryValue) ? 0 : memoryValue;
  const nanParsedCPUValue = Number.isNaN(cpuValue) ? 0 : cpuValue;
  // transform the nodelimits from bytes to MB
  const limits = Object.values(nodeLimits).map((nodeLimit) => ({
    ...nodeLimit,
    Memory: KubernetesResourceReservationHelper.megaBytesValue(
      nodeLimit.Memory
    ),
  }));
  // make sure there's a node available with enough memory and cpu
  return limits.some(
    (nodeLimit) =>
      nodeLimit.Memory >= nanParsedMemoryValue &&
      nodeLimit.CPU >= nanParsedCPUValue
  );
}
